if (!customElements.get('afz-source-reviews')) {
  customElements.define('afz-source-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const dataNode = this.querySelector('[data-source-review-data]');
      if (!dataNode) return;
      try {
        this.data = JSON.parse(dataNode.textContent);
      } catch (_) {
        return;
      }
      for (const partNode of this.querySelectorAll('[data-source-review-part]')) {
        try {
          const part = JSON.parse(partNode.textContent);
          if (!part || typeof part !== 'object') continue;
          if (Array.isArray(part.reviews)) {
            this.data.reviews = Array.isArray(this.data.reviews) ? this.data.reviews.concat(part.reviews) : part.reviews.slice();
          }
          if (Array.isArray(part.photos) && part.photos.length) {
            this.data.photos = Array.isArray(this.data.photos) ? this.data.photos.concat(part.photos) : part.photos.slice();
          }
        } catch (_) {}
      }
      if (this.data.display_version !== 2 || !Array.isArray(this.data.reviews) || !Array.isArray(this.data.groups)) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.rows = this.data.reviews;
      this.sourcePhotos = Array.isArray(this.data.photos) ? this.data.photos : [];
      this.groups = new Map();
      for (const groupData of this.data.groups) {
        const groupNode = [...this.querySelectorAll('[data-source-group-id]')].find(node => node.dataset.sourceGroupId === String(groupData.id));
        if (!groupNode || !Array.isArray(groupData.review_indices)) continue;
        const indices = [...new Set(groupData.review_indices)].filter(index => Number.isInteger(index) && this.eligible(this.rows[index]));
        const list = groupNode.querySelector('[data-source-review-list]');
        if (!list) continue;
        list.replaceChildren();
        const group = { id: String(groupData.id), title: groupData.id === 'product' ? 'Product reviews' : String(groupData.title || 'Customer reviews'), indices, rendered: 0, node: groupNode, list };
        this.groups.set(group.id, group);
        this.appendReviews(group, 8);
      }
      const countNode = this.querySelector('.afz-buyer-show__heading .afz-buyer-show__count');
      if (countNode) countNode.textContent = String(this.rows.length);
      this.dialog = this.querySelector('dialog');
      this.fullPhoto = this.querySelector('[data-source-full]');
      this.reviewPanel = this.querySelector('[data-source-dialog-review]');
      this.photos = [...this.querySelectorAll('.afz-buyer-show__gallery [data-source-photo]')].map(node => ({
        node,
        index: Number(node.dataset.sourcePhoto),
        src: this.safePhotoUrl(node.dataset.sourceFullSrc),
        alt: node.querySelector('img')?.alt || ''
      })).filter(photo => Number.isInteger(photo.index) && photo.src);
      this.classList.add('is-ready');
      const morePhotos = this.querySelector('[data-source-more-photos]');
      if (morePhotos && this.photos.length > 8) morePhotos.hidden = false;
      this.addEventListener('click', event => this.onClick(event), { signal: this.abort.signal });
      if (this.dialog) {
        this.dialog.addEventListener('close', () => this.onDialogClose(), { signal: this.abort.signal });
        this.dialog.addEventListener('keydown', event => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            this.movePhoto(event.key === 'ArrowLeft' ? -1 : 1);
          }
        }, { signal: this.abort.signal });
      }
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.abort?.abort();
      this.initialized = false;
    }

    eligible(row) {
      return Boolean(row && (row.rating === 4 || row.rating === 5));
    }

    safePhotoUrl(value) {
      try {
        const url = new URL(String(value || ''), location.href);
        return url.protocol === 'https:' ? url.href : '';
      } catch (_) {
        return '';
      }
    }

    element(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = String(text);
      return node;
    }

    dateText(value) {
      const date = String(value || '');
      const originalDay = date.match(/^(\d{4}-\d{2}-\d{2})(?:T|$)/)?.[1];
      if (!originalDay) return date;
      const parsed = new Date(`${originalDay}T00:00:00Z`);
      return Number.isNaN(parsed.valueOf()) ? date : new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' }).format(parsed);
    }

    createReview(row, index) {
      const article = this.element('article', 'afz-buyer-show__review');
      article.dataset.sourceReviewCard = String(index);
      article.dataset.sourceReviewId = String(row.source_review_id || '');
      article.dataset.sourceRating = String(row.rating);
      const ratingRow = this.element('div', 'afz-buyer-show__rating-row');
      const stars = this.element('span', 'afz-buyer-show__stars', '★'.repeat(row.rating) + '☆'.repeat(5 - row.rating));
      stars.setAttribute('aria-label', `${row.rating} out of 5 stars`);
      ratingRow.append(stars);
      if (row.date) {
        const time = this.element('time', '', this.dateText(row.date));
        time.setAttribute('datetime', String(row.date));
        ratingRow.append(time);
      }
      article.append(ratingRow, this.element('p', 'afz-buyer-show__author', row.author || ''));
      if (row.title) article.append(this.element('h4', 'afz-buyer-show__review-title', row.title));
      if (row.display_format !== 'rating_only' && row.quote) {
        article.append(this.element('p', 'afz-buyer-show__summary-label', row.is_summary ? 'Review summary' : 'Review excerpt'));
        article.append(this.element('p', 'afz-buyer-show__review-text', row.quote));
      }
      const photoIndices = Array.isArray(row.image_indices) ? row.image_indices : [];
      const thumbnails = this.element('div', 'afz-buyer-show__review-photos');
      for (const photoIndex of photoIndices) {
        const photo = this.sourcePhotos[photoIndex];
        const src = this.safePhotoUrl(photo?.url);
        if (!Number.isInteger(photoIndex) || !src) continue;
        const button = this.element('button', 'afz-buyer-show__review-photo');
        button.type = 'button';
        button.dataset.sourcePhoto = String(photoIndex);
        button.setAttribute('aria-label', `View customer photo: ${photo.alt || row.author || ''}`);
        const image = this.element('img');
        image.src = src;
        image.alt = String(photo.alt || 'Customer photo');
        image.width = Number(photo.width) || 88;
        image.height = Number(photo.height) || 88;
        image.loading = 'lazy';
        image.decoding = 'async';
        button.append(image);
        thumbnails.append(button);
      }
      if (thumbnails.childElementCount) article.append(thumbnails);
      return article;
    }

    appendReviews(group, amount) {
      const end = Math.min(group.rendered + amount, group.indices.length);
      const fragment = document.createDocumentFragment();
      for (let i = group.rendered; i < end; i += 1) {
        const index = group.indices[i];
        fragment.append(this.createReview(this.rows[index], index));
      }
      group.list.append(fragment);
      group.rendered = end;
      const more = group.node.querySelector('[data-source-more-reviews]');
      if (more) {
        more.hidden = end >= group.indices.length;
        more.setAttribute('aria-label', `Load ${Math.min(20, group.indices.length - end)} more reviews in ${group.title}`);
      }
      const progress = group.node.querySelector('[data-source-review-progress]');
      if (progress) progress.textContent = `Showing ${end} of ${group.indices.length} reviews`;
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const loadMore = event.target.closest('[data-source-more-reviews]');
      if (loadMore && this.contains(loadMore)) {
        const group = this.groups.get(loadMore.dataset.sourceMoreReviews);
        if (group) this.appendReviews(group, 20);
        return;
      }
      if (event.target.closest('[data-source-more-photos]')) {
        this.classList.add('is-expanded');
        this.querySelector('[data-source-more-photos]').hidden = true;
        return;
      }
      const photo = event.target.closest('[data-source-photo]');
      if (photo && this.contains(photo) && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        this.openPhoto(Number(photo.dataset.sourcePhoto), photo);
        return;
      }
      if (event.target.closest('[data-source-close]')) this.dialog?.close();
      if (event.target.closest('[data-source-prev]')) this.movePhoto(-1);
      if (event.target.closest('[data-source-next]')) this.movePhoto(1);
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }
    }

    openPhoto(photoIndex, trigger) {
      if (!this.dialog || !this.fullPhoto || !this.reviewPanel) return;
      const index = this.photos.findIndex(photo => photo.index === photoIndex);
      if (index < 0) return;
      this.index = index;
      this.renderPhoto();
      if (!this.dialog.open) {
        this.returnFocus = trigger;
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.dialog.showModal();
      }
    }

    movePhoto(direction) {
      if (!this.photos.length || !this.dialog?.open) return;
      this.index = (this.index + direction + this.photos.length) % this.photos.length;
      this.renderPhoto();
    }

    renderPhoto() {
      const photo = this.photos[this.index];
      if (!photo) return;
      this.fullPhoto.src = photo.src;
      this.fullPhoto.alt = photo.alt;
      this.querySelector('[data-source-photo-counter]').textContent = `${this.index + 1} / ${this.photos.length}`;
      this.querySelectorAll('[data-source-prev], [data-source-next]').forEach(button => { button.disabled = this.photos.length < 2; });
      const reviewIndex = this.rows.findIndex(row => this.eligible(row) && Array.isArray(row.image_indices) && row.image_indices.includes(photo.index));
      this.reviewPanel.replaceChildren();
      this.reviewPanel.hidden = reviewIndex < 0;
      this.querySelector('.afz-buyer-show__dialog-body').classList.toggle('is-photo-only', reviewIndex < 0);
      if (reviewIndex >= 0) this.reviewPanel.append(this.createReview(this.rows[reviewIndex], reviewIndex));
    }

    onDialogClose() {
      document.body.style.overflow = this.previousOverflow || '';
      this.fullPhoto?.removeAttribute('src');
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    }
  });
}
